import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { useFormacao } from '@/context/FormacaoContext';
import { calcularProgressao, calcularPontosCiclo, calcularLimiarFuncionario, pontosParaAvaliacao, PONTOS_HISTORICO, type AvaliacaoSiadap, type PontosEscalasConfig } from '@/utils/progressaoUtils';
import SiadapTab from './components/SiadapTab';
import PontosManuaisModal from '@/pages/funcionarios/components/PontosManuaisModal';
import RegistarProgressaoModal from '@/pages/home/components/RegistarProgressaoModal';

interface FuncionarioDetalhe {
  id: string;
  nome: string;
  carreira: string;
  sexo: string;
  nivel_atual: number;
  novo_nivel_2025: number;
  vencimento: number;
  unidade_organica: string;
  subunidade_organica: string;
  ultima_progressao: string | null;
  data_entrada_funcao_publica: string | null;
  avaliacao: string;
  pontos_atuais: number;
  pontos_descontados: number;
  excecao_prepav: boolean;
  descricao_subida: string | null;
  // Campos extra
  email: string | null;
  telefone: string | null;
  data_nascimento: string | null;
  cpf: string | null;
  endereco: string | null;
  status: string | null;
  superior_hierarquico: string | null;
  categoria: string | null;
  despacho_autorizacao: string | null;
  certificados: string | null;
}

const ESTADO_CONFIG: Record<string, { bg: string; text: string; dot: string }> = {
  Ativo:      { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  Inativo:    { bg: 'bg-zinc-100',   text: 'text-zinc-500',    dot: 'bg-zinc-400' },
  'Licença':  { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-400' },
  Aposentado: { bg: 'bg-slate-100',  text: 'text-slate-500',   dot: 'bg-slate-400' },
  'Cedência': { bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-400' },
  Mobilidade: { bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-400' },
};

function formatDate(d: string | null): string {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function calcularIdade(dataNasc: string | null): number | null {
  if (!dataNasc) return null;
  const nasc = new Date(dataNasc);
  if (isNaN(nasc.getTime())) return null;
  const hoje = new Date();
  let idade = hoje.getFullYear() - nasc.getFullYear();
  if (hoje.getMonth() < nasc.getMonth() || (hoje.getMonth() === nasc.getMonth() && hoje.getDate() < nasc.getDate())) idade--;
  return Math.max(0, idade);
}

function calcularAnosServico(data: string | null): number | null {
  if (!data) return null;
  const entrada = new Date(data);
  if (isNaN(entrada.getTime())) return null;
  const hoje = new Date();
  let anos = hoje.getFullYear() - entrada.getFullYear();
  if (hoje.getMonth() < entrada.getMonth() || (hoje.getMonth() === entrada.getMonth() && hoje.getDate() < entrada.getDate())) anos--;
  return Math.max(0, anos);
}

function InfoRow({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-zinc-50 last:border-0">
      <span className="text-[12px] font-semibold text-zinc-400 flex-shrink-0 w-40">{label}</span>
      <span className={`text-[13px] text-zinc-700 text-right flex-1 ${mono ? 'font-mono' : ''}`}>{value || <span className="text-zinc-300">—</span>}</span>
    </div>
  );
}

export default function FuncionarioDetalhePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { funcionarios, reload: reloadFuncionarios } = useFuncionarios();
  const { registosSiadap, registosHistorico, progressoes, pontosDescontadosPorFuncionario, reloadSiadap } = useSiadap();
  const { settings } = useAppSettings();
  const { participantes: todasParticipacoes, formacoes } = useFormacao();

  const jaUsouAcelerador = useMemo(() =>
    progressoes.some((p) => p.funcionarioId === id && p.aceleradorRemuneratorio),
    [progressoes, id]
  );
  const [detalhe, setDetalhe] = useState<FuncionarioDetalhe | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'pessoal' | 'carreira' | 'progressao' | 'siadap' | 'formacao'>('pessoal');
  const [showPontosManuais, setShowPontosManuais] = useState(false);
  const [showRegistarProgressao, setShowRegistarProgressao] = useState(false);
  const [progressaoSuccessMsg, setProgressaoSuccessMsg] = useState('');
  const [printLoading, setPrintLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    supabase
      .from('funcionarios')
      .select('*')
      .eq('id', id)
      .maybeSingle()
      .then(({ data }) => {
        setDetalhe(data as FuncionarioDetalhe | null);
        setLoading(false);
      });
  }, [id]);

  const funcionario = funcionarios.find((f) => f.id === id);

  // Recarregar detalhe quando refreshKey muda (após progressão ou ajuste manual)
  useEffect(() => {
    if (!id) return;
    supabase
      .from('funcionarios')
      .select('*')
      .eq('id', id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setDetalhe(data as FuncionarioDetalhe);
      });
  }, [id, refreshKey]);

  const escalas: PontosEscalasConfig = {
    pos2025: {
      Excelente: settings.pontos_excelente,
      'Muito Bom': settings.pontos_muito_bom,
      Bom: settings.pontos_bom,
      Regular: settings.pontos_regular,
      Inadequado: settings.pontos_inadequado,
    },
    p2023_2024: {
      Excelente: settings.pontos_excelente_2023,
      'Muito Bom': settings.pontos_muito_bom_2023,
      Bom: settings.pontos_bom_2023,
      Regular: settings.pontos_regular_2023,
      Inadequado: settings.pontos_inadequado_2023,
    },
    pre2023: {
      Excelente: settings.pontos_excelente_pre2023,
      Relevante: settings.pontos_relevante_pre2023,
      Adequado: settings.pontos_adequado_pre2023,
      Inadequado: settings.pontos_inadequado_pre2023,
      'Muito Bom': settings.pontos_relevante_pre2023,
      Bom: settings.pontos_adequado_pre2023,
      Regular: settings.pontos_adequado_pre2023,
    },
  };

  const pontosMap: Partial<Record<AvaliacaoSiadap, number>> = {
    Excelente: settings.pontos_excelente,
    'Muito Bom': settings.pontos_muito_bom,
    Bom: settings.pontos_bom,
    Regular: settings.pontos_regular,
    Inadequado: settings.pontos_inadequado,
  };

  const siadapDoFunc = registosSiadap.filter((r) => r.funcionarioId === id);
  const historicoDoFunc = registosHistorico.filter((r) => r.funcionarioId === id);
  // Soma real das progressões é a fonte de verdade. Adiciona manuais se forem realmente adicionais.
  const descontosProgressoes = pontosDescontadosPorFuncionario[id ?? ''] ?? 0;
  const descontosManuais = detalhe?.pontos_descontados ?? 0;
  let pontosDescontados: number;
  if (descontosManuais > descontosProgressoes) {
    pontosDescontados = descontosManuais;
  } else if (descontosManuais === descontosProgressoes) {
    pontosDescontados = descontosProgressoes;
  } else {
    pontosDescontados = descontosProgressoes + descontosManuais;
  }
  const isPrepav = detalhe?.excecao_prepav === true || funcionario?.excecaoPrepav === true;
  const temRegistos = siadapDoFunc.length > 0 || historicoDoFunc.length > 0;
  // Pontos manuais são SEMPRE somados (independentemente de PREPAV ou ter registos SIADAP)
  // O campo pontos_atuais na BD representa ajustes manuais que acumulam com os pontos SIADAP
  const pontosManuais = funcionario?.pontosAtuais ?? detalhe?.pontos_atuais ?? 0;
  const pontosAtuais = detalhe
    ? calcularPontosCiclo(siadapDoFunc, historicoDoFunc, pontosDescontados, pontosManuais, pontosMap, undefined, escalas)
    : 0;

  // Pontos por fonte (para o resumo)
  const pontosSiadap = useMemo(() =>
    siadapDoFunc.reduce((s, r) => s + pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas), 0),
    [siadapDoFunc, pontosMap, escalas]
  );
  const pontosHistorico = useMemo(() =>
    historicoDoFunc.reduce((s, r) => s + (PONTOS_HISTORICO[r.classificacaoServico] ?? 0), 0),
    [historicoDoFunc]
  );

  const { limiar } = detalhe
    ? calcularLimiarFuncionario(siadapDoFunc, historicoDoFunc, settings.pontos_progressao, settings.pontos_progressao_pre2025, settings.pontos_progressao_2023, escalas)
    : { limiar: settings.pontos_progressao };

  const progressao = detalhe
    ? calcularProgressao(pontosAtuais, detalhe.nivel_atual, limiar)
    : null;

  // Verificar se há múltiplas progressões pendentes (pontos >= 2x limiar)
  const progressoesPendentes = detalhe && limiar > 0 ? Math.floor(pontosAtuais / limiar) : 0;
  const temMultiplasProgressoes = progressoesPendentes >= 2;

  const initials = (detalhe?.nome ?? '')
    .split(' ')
    .filter((_, i, arr) => i === 0 || i === arr.length - 1)
    .map((n) => n[0])
    .join('').toUpperCase();

  const estadoCfg = detalhe?.status ? (ESTADO_CONFIG[detalhe.status] ?? { bg: 'bg-zinc-100', text: 'text-zinc-500', dot: 'bg-zinc-400' }) : null;

  // Formações deste funcionário
  const formacoesFuncionario = useMemo(() => {
    return todasParticipacoes
      .filter((p) => p.funcionario_id === id)
      .map((p) => {
        const f = formacoes.find((f) => f.id === p.formacao_id);
        return { participacao: p, formacao: f ?? null };
      });
  }, [todasParticipacoes, formacoes, id]);

  const totalHorasFormacao = useMemo(() =>
    formacoesFuncionario.reduce((s, item) => s + (item.formacao?.duracao_horas ?? 0), 0),
    [formacoesFuncionario]
  );

  const tabs = [
    { key: 'pessoal', label: 'Dados Pessoais', icon: 'ri-user-line' },
    { key: 'carreira', label: 'Carreira & Função', icon: 'ri-briefcase-line' },
    { key: 'progressao', label: 'Progressão', icon: 'ri-arrow-up-circle-line' },
    { key: 'siadap', label: 'SIADAP', icon: 'ri-award-line', badge: siadapDoFunc.length || undefined },
    { key: 'formacao', label: 'Formação', icon: 'ri-book-open-line', badge: formacoesFuncionario.length || undefined },
  ] as const;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line text-3xl text-zinc-300 animate-spin" />
          <p className="text-[13px] text-zinc-400">A carregar ficha...</p>
        </div>
      </div>
    );
  }

  if (!detalhe) {
    return (
      <div className="flex-1 flex items-center justify-center bg-zinc-50">
        <div className="flex flex-col items-center gap-3 text-zinc-400">
          <i className="ri-user-unfollow-line text-4xl" />
          <p className="text-[14px] font-medium">Funcionário não encontrado</p>
          <button onClick={() => navigate('/funcionarios')} className="whitespace-nowrap text-[13px] text-zinc-500 underline cursor-pointer">
            Voltar à lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <main className="flex-1 bg-zinc-50/60 min-h-screen" ref={printRef}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white !important; font-family: 'Segoe UI', Arial, sans-serif; }
          main { background: white !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
        .print-only { display: none; }
      `}</style>

      {/* ── PRINT HEADER (só visível na impressão) ── */}
      <div className="print-only px-8 py-6 border-b border-zinc-200 mb-2">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400 mb-1">{settings.nome_camara || 'Câmara Municipal'} · SIADAP para RH</p>
            <h1 className="text-[22px] font-bold text-zinc-900">{detalhe?.nome}</h1>
            <p className="text-[12px] text-zinc-500 mt-0.5">
              {detalhe?.carreira}{detalhe?.unidade_organica ? ` · ${detalhe.unidade_organica}` : ''} · Nível {detalhe?.nivel_atual}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-zinc-400">Ficha gerada em</p>
            <p className="text-[13px] font-semibold text-zinc-700">{new Date().toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
            <p className="text-[11px] text-zinc-400 mt-1">Nº {detalhe?.id}</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-zinc-100">
          {[
            { label: 'Pontos Acumulados', value: `${pontosAtuais.toFixed(1)} pts` },
            { label: 'Limiar de Progressão', value: `${limiar} pts` },
            { label: 'Vencimento', value: (detalhe?.vencimento ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }) },
            { label: 'Última Progressão', value: formatDate(detalhe?.ultima_progressao ?? null) },
          ].map((k) => (
            <div key={k.label} className="bg-zinc-50 rounded-lg p-3">
              <p className="text-[10px] text-zinc-400 uppercase tracking-wide">{k.label}</p>
              <p className="text-[15px] font-bold text-zinc-800 mt-0.5">{k.value}</p>
            </div>
          ))}
        </div>
      </div>
      {/* ── HEADER ── */}
      <div className="bg-white border-b border-zinc-100 px-8 py-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[12px] text-zinc-400 mb-4">
          <Link to="/funcionarios" className="hover:text-zinc-700 transition-colors cursor-pointer">Funcionários</Link>
          <i className="ri-arrow-right-s-line text-zinc-300" />
          <span className="text-zinc-600 font-medium truncate">{detalhe.nome}</span>
        </div>

        <div className="flex items-start justify-between gap-6 flex-wrap">
          {/* Avatar + info principal */}
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-zinc-900 text-white text-xl font-bold flex-shrink-0">
              {initials}
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1 flex-wrap">
                <h1 className="text-[22px] font-bold text-zinc-900 tracking-tight">{detalhe.nome}</h1>
                {estadoCfg && detalhe.status && (
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold ${estadoCfg.bg} ${estadoCfg.text}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${estadoCfg.dot}`} />
                    {detalhe.status}
                  </span>
                )}
                {isPrepav && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-orange-100 text-orange-700">
                    <i className="ri-star-line text-xs" />PREPAV
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[13px] text-zinc-500 font-mono">#{detalhe.id}</span>
                <span className="text-zinc-200">·</span>
                <span className="text-[13px] text-zinc-500">{detalhe.carreira}</span>
                {detalhe.unidade_organica && (
                  <>
                    <span className="text-zinc-200">·</span>
                    <span className="text-[13px] text-zinc-400">{detalhe.unidade_organica}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Ações */}
          <div className="flex items-center gap-2 flex-shrink-0 no-print">
            <button
              onClick={() => navigate('/funcionarios')}
              className="whitespace-nowrap flex items-center gap-2 border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 text-[13px] font-medium px-4 py-2.5 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-sm" />
              Voltar
            </button>
            <button
              onClick={() => {
                setPrintLoading(true);
                setTimeout(() => { window.print(); setPrintLoading(false); }, 200);
              }}
              disabled={printLoading}
              className="whitespace-nowrap flex items-center gap-2 border border-zinc-200 bg-white text-zinc-600 hover:text-zinc-900 text-[13px] font-medium px-4 py-2.5 rounded-lg transition-colors cursor-pointer disabled:opacity-60"
            >
              <i className={`${printLoading ? 'ri-loader-4-line animate-spin' : 'ri-printer-line'} text-sm`} />
              Imprimir Ficha
            </button>
          </div>
        </div>

        {/* KPIs rápidos */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {[
            {
              icon: 'ri-arrow-up-circle-line',
              label: 'Nível Atual',
              value: `${detalhe.nivel_atual} → ${detalhe.novo_nivel_2025}`,
              sub: 'Nível 2025',
              color: 'text-zinc-900',
            },
            {
              icon: 'ri-bar-chart-line',
              label: 'Pontos Acumulados',
              value: `${pontosAtuais.toFixed(1)} pts`,
              sub: progressao ? `${progressao.percentagem.toFixed(0)}% do limiar (${limiar} pts)` : '—',
              color: progressao?.status === 'pronto' ? 'text-emerald-700' : 'text-zinc-900',
            },
            {
              icon: 'ri-money-euro-circle-line',
              label: 'Vencimento',
              value: (detalhe.vencimento ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }),
              sub: `${((detalhe.vencimento ?? 0) * 14).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })} / ano`,
              color: 'text-zinc-900',
            },
            {
              icon: 'ri-time-line',
              label: 'Anos de Serviço',
              value: calcularAnosServico(detalhe.data_entrada_funcao_publica) !== null ? `${calcularAnosServico(detalhe.data_entrada_funcao_publica)} anos` : '—',
              sub: detalhe.data_entrada_funcao_publica ? `Desde ${formatDate(detalhe.data_entrada_funcao_publica)}` : 'Data não registada',
              color: 'text-zinc-900',
            },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-zinc-50 rounded-xl p-4 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg border border-zinc-100 flex-shrink-0">
                <i className={`${kpi.icon} text-zinc-400 text-base`} />
              </div>
              <div className="min-w-0">
                <p className={`text-[15px] font-bold leading-tight ${kpi.color}`}>{kpi.value}</p>
                <p className="text-[10px] text-zinc-400 mt-0.5 leading-tight">{kpi.label}</p>
                <p className="text-[10px] text-zinc-300 leading-tight truncate">{kpi.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── TABS ── */}
      <div className="bg-white border-b border-zinc-100 px-8 no-print">
        <div className="flex items-center gap-0">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`whitespace-nowrap flex items-center gap-2 px-5 py-3.5 text-[13px] font-medium border-b-2 transition-all cursor-pointer ${
                activeTab === t.key
                  ? 'border-zinc-900 text-zinc-900'
                  : 'border-transparent text-zinc-400 hover:text-zinc-700'
              }`}
            >
              <i className={`${t.icon} text-sm`} />
              {t.label}
              {'badge' in t && (t as { badge?: number }).badge ? (
                <span className="text-[10px] font-bold bg-zinc-900 text-white px-1.5 py-0.5 rounded-full">
                  {(t as { badge?: number }).badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div className="px-8 py-6 max-w-5xl">

        {/* ── DADOS PESSOAIS ── */}
        {activeTab === 'pessoal' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Identificação */}
            <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <i className="ri-id-card-line text-zinc-500 text-sm" />
                </div>
                <h2 className="text-[13px] font-semibold text-zinc-700">Identificação</h2>
              </div>
              <div className="px-5 py-2">
                <InfoRow label="Nº Funcionário" value={<span className="font-mono">#{detalhe.id}</span>} />
                <InfoRow label="Nome Completo" value={detalhe.nome} />
                <InfoRow label="Género" value={detalhe.sexo === 'M' ? 'Masculino' : 'Feminino'} />
                <InfoRow label="Data de Nascimento" value={
                  detalhe.data_nascimento
                    ? `${formatDate(detalhe.data_nascimento)}${calcularIdade(detalhe.data_nascimento) !== null ? ` (${calcularIdade(detalhe.data_nascimento)} anos)` : ''}`
                    : null
                } />
                <InfoRow label="NIF / CPF" value={detalhe.cpf} mono />
              </div>
            </div>

            {/* Contacto */}
            <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <i className="ri-phone-line text-zinc-500 text-sm" />
                </div>
                <h2 className="text-[13px] font-semibold text-zinc-700">Contacto</h2>
              </div>
              <div className="px-5 py-2">
                <InfoRow label="Email" value={
                  detalhe.email
                    ? <a href={`mailto:${detalhe.email}`} className="text-zinc-700 hover:text-zinc-900 underline underline-offset-2 cursor-pointer">{detalhe.email}</a>
                    : null
                } />
                <InfoRow label="Telefone" value={
                  detalhe.telefone
                    ? <a href={`tel:${detalhe.telefone}`} className="text-zinc-700 hover:text-zinc-900 cursor-pointer">{detalhe.telefone}</a>
                    : null
                } />
                <InfoRow label="Morada" value={detalhe.endereco} />
              </div>
            </div>
          </div>
        )}

        {/* ── CARREIRA & FUNÇÃO ── */}
        {activeTab === 'carreira' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Carreira */}
            <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <i className="ri-briefcase-line text-zinc-500 text-sm" />
                </div>
                <h2 className="text-[13px] font-semibold text-zinc-700">Carreira</h2>
              </div>
              <div className="px-5 py-2">
                <InfoRow label="Carreira" value={detalhe.carreira} />
                <InfoRow label="Categoria" value={detalhe.categoria} />
                <InfoRow label="Estado" value={
                  detalhe.status && estadoCfg
                    ? <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${estadoCfg.bg} ${estadoCfg.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${estadoCfg.dot}`} />
                        {detalhe.status}
                      </span>
                    : null
                } />
                <InfoRow label="Unidade Orgânica" value={detalhe.unidade_organica} />
                <InfoRow label="Subunidade" value={detalhe.subunidade_organica} />
                <InfoRow label="Superior Hierárquico" value={detalhe.superior_hierarquico} />
              </div>
            </div>

            {/* Função Pública */}
            <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <i className="ri-government-line text-zinc-500 text-sm" />
                </div>
                <h2 className="text-[13px] font-semibold text-zinc-700">Função Pública</h2>
              </div>
              <div className="px-5 py-2">
                <InfoRow label="Entrada na F.P." value={
                  detalhe.data_entrada_funcao_publica
                    ? `${formatDate(detalhe.data_entrada_funcao_publica)} (${calcularAnosServico(detalhe.data_entrada_funcao_publica)} anos)`
                    : null
                } />
                <InfoRow label="Despacho Autorização" value={detalhe.despacho_autorizacao} />
                <InfoRow label="Vencimento" value={(detalhe.vencimento ?? 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' })} />
                <InfoRow label="Exceção PREPAV" value={
                  detalhe.excecao_prepav
                    ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full"><i className="ri-star-line text-xs" />Ativa</span>
                    : <span className="text-zinc-300 text-xs">Não aplicável</span>
                } />
              </div>
              {detalhe.certificados && (
                <div className="px-5 pb-4">
                  <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">Certificados / Habilitações</p>
                  <p className="text-[13px] text-zinc-600 leading-relaxed bg-zinc-50 rounded-lg p-3">{detalhe.certificados}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── PROGRESSÃO ── */}
        {activeTab === 'progressao' && (
          <div className="space-y-5">

            {/* Toast de sucesso */}
            {progressaoSuccessMsg && (
              <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 text-emerald-700 text-[13px] px-4 py-3 rounded-xl">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-base" />
                </div>
                {progressaoSuccessMsg}
              </div>
            )}

            {/* Barra de progressão grande */}
            <div className="bg-white rounded-2xl border border-zinc-100 p-6">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <h2 className="text-[15px] font-bold text-zinc-800">Estado de Progressão</h2>
                  <p className="text-[12px] text-zinc-400 mt-0.5">Ciclo avaliativo atual</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap no-print">
                  {funcionario && (
                    <button
                      onClick={() => setShowPontosManuais(true)}
                      className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 border border-zinc-200 bg-white hover:bg-zinc-50 px-3 py-2 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-coins-line text-sm" />
                      Ajuste Manual
                    </button>
                  )}
                  {funcionario && (
                    <button
                      onClick={() => setShowRegistarProgressao(true)}
                      className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-semibold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-2 rounded-lg transition-colors cursor-pointer"
                    >
                      <i className="ri-arrow-up-circle-line text-sm" />
                      Registar Progressão
                    </button>
                  )}
                  <Link
                    to={`/funcionarios/${id}/relatorio`}
                    className="whitespace-nowrap flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 border border-zinc-200 bg-white hover:bg-zinc-50 px-3 py-2 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-file-chart-line text-sm" />
                    Ver Relatório
                  </Link>
                </div>
              </div>

              {/* Badge de estado */}
              {progressao && (
                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] font-bold mb-4 ${
                  progressao.status === 'pronto' ? 'bg-emerald-100 text-emerald-700' :
                  progressao.status === 'proximo' ? 'bg-amber-100 text-amber-700' :
                  'bg-zinc-100 text-zinc-600'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${
                    progressao.status === 'pronto' ? 'bg-emerald-500 animate-pulse' :
                    progressao.status === 'proximo' ? 'bg-amber-500' : 'bg-zinc-400'
                  }`} />
                  {progressao.status === 'pronto' ? 'Elegível para Progressão' :
                   progressao.status === 'proximo' ? 'Próximo da Progressão' : 'Em Progresso'}
                </div>
              )}

              {/* Alerta de múltiplas progressões pendentes */}
              {temMultiplasProgressoes && (
                <div className="mb-4 rounded-xl border border-amber-200/80 overflow-hidden" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' }}>
                  <div className="flex items-center gap-2.5 px-4 py-3">
                    <div className="w-6 h-6 flex items-center justify-center bg-amber-500 rounded-md text-white flex-shrink-0">
                      <i className="ri-repeat-line text-xs" />
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[12px] font-bold text-amber-900">
                        {progressoesPendentes} progressões pendentes
                      </span>
                      <span className="text-[12px] text-amber-700">
                        ({pontosAtuais.toFixed(1)} pts ≥ {progressoesPendentes}×{limiar} pts)
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">
                        <i className="ri-error-warning-line text-[9px]" />
                        Registar todas
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {progressao && (
                <>
                  <div className="flex items-end justify-between mb-2">
                    <div>
                      <span className="text-[38px] font-bold text-zinc-900 leading-none">{pontosAtuais.toFixed(1)}</span>
                      <span className="text-[16px] text-zinc-400 ml-2">pts</span>
                    </div>
                    <span className="text-[13px] text-zinc-400 mb-1">/ {limiar} pts necessários</span>
                  </div>
                  <div className="h-3 bg-zinc-100 rounded-full overflow-hidden mb-3">
                    <div
                      className={`h-full rounded-full transition-all ${
                        progressao.status === 'pronto' ? 'bg-emerald-500' :
                        progressao.status === 'proximo' ? 'bg-amber-400' : 'bg-zinc-400'
                      }`}
                      style={{ width: `${Math.min(100, progressao.percentagem)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[12px] text-zinc-400">
                    <span>{progressao.percentagem.toFixed(0)}% concluído</span>
                    {progressao.status !== 'pronto' && (
                      <span>Faltam <strong className="text-zinc-600">{progressao.pontosFaltam.toFixed(1)} pts</strong></span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ── PAINEL DE DEBUG (cálculo passo a passo) ── */}
            <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-zinc-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center bg-zinc-100 rounded-lg">
                    <i className="ri-bug-line text-zinc-400 text-xs" />
                  </div>
                  <h2 className="text-[12px] font-semibold text-zinc-500">Cálculo de Pontos (Debug)</h2>
                </div>
                <span className="text-[10px] text-zinc-300 font-mono">refreshKey: {refreshKey}</span>
              </div>
              <div className="px-5 py-3 space-y-2 text-[11px] font-mono text-zinc-500">
                <div className="flex justify-between">
                  <span>Pontos SIADAP:</span>
                  <span className="text-zinc-700">{pontosSiadap.toFixed(1)} pts ({siadapDoFunc.length} registos)</span>
                </div>
                <div className="flex justify-between">
                  <span>Pontos Histórico:</span>
                  <span className="text-zinc-700">{pontosHistorico.toFixed(1)} pts ({historicoDoFunc.length} registos)</span>
                </div>
                <div className="flex justify-between">
                  <span>Pontos Manuais{isPrepav ? ' (PREPAV)' : ''}:</span>
                  <span className="text-zinc-700">{pontosManuais.toFixed(1)} pts</span>
                </div>
                <div className="flex justify-between border-t border-zinc-100 pt-1">
                  <span>Subtotal bruto:</span>
                  <span className="text-zinc-700">{(pontosSiadap + pontosHistorico + pontosManuais).toFixed(1)} pts</span>
                </div>
                <div className="flex justify-between">
                  <span>Pontos Descontados:</span>
                  <span className="text-rose-600">−{pontosDescontados.toFixed(1)} pts</span>
                </div>
                <div className="flex justify-between border-t border-zinc-100 pt-1">
                  <span className="font-bold text-zinc-700">Pontos Líquidos:</span>
                  <span className="font-bold text-zinc-900">{pontosAtuais.toFixed(1)} pts</span>
                </div>
                <div className="flex justify-between">
                  <span>Limiar:</span>
                  <span className="text-zinc-700">{limiar} pts</span>
                </div>
                <div className="flex justify-between">
                  <span>Progressões possíveis:</span>
                  <span className={progressoesPendentes >= 1 ? 'text-emerald-600 font-bold' : 'text-zinc-700'}>
                    {progressoesPendentes}× (pts/limiar = {(limiar > 0 ? pontosAtuais / limiar : 0).toFixed(2)})
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={progressao?.status === 'pronto' ? 'text-emerald-600 font-bold' : 'text-zinc-700'}>
                    {progressao?.status ?? '—'}
                  </span>
                </div>
                <div className="flex justify-between text-[10px] text-zinc-300 pt-1 border-t border-zinc-50">
                  <span>Fonte pontos_descontados:</span>
                  <span>
                    {detalhe?.pontos_descontados !== undefined ? `detalhe (${detalhe.pontos_descontados})` :
                     pontosDescontadosPorFuncionario[id ?? ''] !== undefined ? `contexto (${pontosDescontadosPorFuncionario[id ?? '']})` :
                     funcionario?.pontosDescontados !== undefined ? `funcionario (${funcionario.pontosDescontados})` : 'fallback (0)'}
                  </span>
                </div>
              </div>
            </div>

            {/* ── RESUMO DE PONTOS POR FONTE ── */}
            <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                  <i className="ri-pie-chart-line text-zinc-500 text-sm" />
                </div>
                <h2 className="text-[13px] font-semibold text-zinc-700">Origem dos Pontos</h2>
                <span className="ml-auto text-[11px] text-zinc-400">Total: <strong className="text-zinc-700">{pontosAtuais.toFixed(1)} pts</strong></span>
              </div>
              <div className="px-5 py-4 space-y-4">
                {/* Barra de distribuição */}
                {pontosAtuais > 0 && (
                  <div className="h-2.5 bg-zinc-100 rounded-full overflow-hidden flex">
                    {pontosSiadap > 0 && (
                      <div className="h-full bg-emerald-500 transition-all" style={{ width: `${Math.min(100, (pontosSiadap / pontosAtuais) * 100)}%` }} />
                    )}
                    {pontosHistorico > 0 && (
                      <div className="h-full bg-amber-400 transition-all" style={{ width: `${Math.min(100, (pontosHistorico / pontosAtuais) * 100)}%` }} />
                    )}
                    {pontosManuais > 0 && (
                      <div className="h-full bg-zinc-400 transition-all" style={{ width: `${Math.min(100, (pontosManuais / pontosAtuais) * 100)}%` }} />
                    )}
                  </div>
                )}

                {/* Legenda */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    {
                      icon: 'ri-award-line',
                      label: 'Avaliações SIADAP',
                      value: pontosSiadap,
                      count: siadapDoFunc.length,
                      countLabel: 'registo(s)',
                      dot: 'bg-emerald-500',
                      bg: 'bg-emerald-50',
                      text: 'text-emerald-700',
                      empty: siadapDoFunc.length === 0,
                    },
                    {
                      icon: 'ri-history-line',
                      label: 'Histórico Importado',
                      value: pontosHistorico,
                      count: historicoDoFunc.length,
                      countLabel: 'registo(s)',
                      dot: 'bg-amber-400',
                      bg: 'bg-amber-50',
                      text: 'text-amber-700',
                      empty: historicoDoFunc.length === 0,
                    },
                    {
                      icon: 'ri-coins-line',
                      label: 'Pontos Manuais',
                      value: pontosManuais,
                      count: null,
                      countLabel: isPrepav ? 'PREPAV' : 'ajuste',
                      dot: 'bg-zinc-400',
                      bg: 'bg-zinc-100',
                      text: 'text-zinc-600',
                      empty: pontosManuais === 0,
                    },
                  ].map((s) => (
                    <div key={s.label} className={`rounded-xl p-4 border ${
                      s.empty ? 'border-zinc-100 bg-zinc-50/50' : `border-zinc-100 ${s.bg}`
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${s.empty ? 'bg-zinc-200' : s.dot}`} />
                        <span className={`text-[11px] font-semibold ${s.empty ? 'text-zinc-400' : s.text}`}>{s.label}</span>
                      </div>
                      <p className={`text-[22px] font-bold leading-tight ${s.empty ? 'text-zinc-300' : 'text-zinc-800'}`}>
                        {s.value.toFixed(1)}
                        <span className="text-[12px] font-normal text-zinc-400 ml-1">pts</span>
                      </p>
                      <p className="text-[11px] text-zinc-400 mt-0.5">
                        {s.count !== null ? `${s.count} ${s.countLabel}` : s.countLabel}
                      </p>
                    </div>
                  ))}
                </div>

                {/* Descontos */}
                {pontosDescontados > 0 && (
                  <div className="flex items-center justify-between pt-3 border-t border-zinc-100">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center text-rose-400">
                        <i className="ri-subtract-line text-sm" />
                      </div>
                      <span className="text-[12px] text-zinc-500">Pontos descontados (progressões anteriores)</span>
                    </div>
                    <span className="text-[13px] font-bold text-rose-600">−{pontosDescontados.toFixed(1)} pts</span>
                  </div>
                )}
              </div>
            </div>

            {/* Detalhes de progressão */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                    <i className="ri-arrow-up-circle-line text-zinc-500 text-sm" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-zinc-700">Níveis</h2>
                </div>
                <div className="px-5 py-2">
                  <InfoRow label="Nível Atual" value={detalhe.nivel_atual} />
                  <InfoRow label="Novo Nível 2025" value={detalhe.novo_nivel_2025} />
                  <InfoRow label="Última Progressão" value={formatDate(detalhe.ultima_progressao)} />
                  <InfoRow label="Pontos Descontados" value={`${detalhe.pontos_descontados ?? 0} pts`} />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                    <i className="ri-file-text-line text-zinc-500 text-sm" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-zinc-700">Observações</h2>
                </div>
                <div className="px-5 py-4">
                  {detalhe.descricao_subida
                    ? <p className="text-[13px] text-zinc-600 leading-relaxed">{detalhe.descricao_subida}</p>
                    : <p className="text-[13px] text-zinc-300">Sem observações registadas.</p>
                  }
                </div>
              </div>
            </div>

            {/* Avaliações SIADAP recentes (resumo) */}
            {siadapDoFunc.length > 0 && (
              <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-zinc-50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                      <i className="ri-award-line text-zinc-500 text-sm" />
                    </div>
                    <h2 className="text-[13px] font-semibold text-zinc-700">Avaliações SIADAP ({siadapDoFunc.length})</h2>
                  </div>
                  <button
                    onClick={() => setActiveTab('siadap')}
                    className="whitespace-nowrap text-[12px] font-medium text-zinc-500 hover:text-zinc-800 flex items-center gap-1 transition-colors cursor-pointer no-print"
                  >
                    Ver todas <i className="ri-arrow-right-line text-sm" />
                  </button>
                </div>
                <div className="divide-y divide-zinc-50">
                  {[...siadapDoFunc]
                    .sort((a, b) => b.periodoAvaliacao.localeCompare(a.periodoAvaliacao))
                    .slice(0, 4)
                    .map((r) => {
                      const avalCores: Record<string, { bg: string; text: string; dot: string }> = {
                        Excelente:   { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
                        'Muito Bom': { bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500'   },
                        Bom:         { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
                        Regular:     { bg: 'bg-orange-50',  text: 'text-orange-700',  dot: 'bg-orange-400'  },
                        Inadequado:  { bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500'    },
                        Relevante:   { bg: 'bg-teal-50',    text: 'text-teal-700',    dot: 'bg-teal-500'    },
                        Adequado:    { bg: 'bg-zinc-100',   text: 'text-zinc-600',    dot: 'bg-zinc-400'    },
                      };
                      const cfg = avalCores[r.avaliacao] ?? avalCores['Bom'];
                      const pts = pontosParaAvaliacao(r.avaliacao, r.periodoAvaliacao ?? '', pontosMap, escalas);
                      return (
                        <div key={r.id} className="flex items-center justify-between px-5 py-3 hover:bg-zinc-50/60 transition-colors">
                          <div className="flex items-center gap-3">
                            <span className="text-[13px] font-bold text-zinc-700 w-24 flex-shrink-0">{r.periodoAvaliacao}</span>
                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                              {r.avaliacao}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-[12px] text-zinc-500">
                            <span>Chefia: <strong className="text-zinc-700">{r.pontosChefe.toFixed(1)}</strong></span>
                            <span>CCA: <strong className="text-zinc-700">{r.pontosCCA.toFixed(1)}</strong></span>
                            <span className="font-bold text-zinc-800">{pts.toFixed(1)} pts</span>
                          </div>
                        </div>
                      );
                    })}
                </div>
                {siadapDoFunc.length > 4 && (
                  <div className="px-5 py-3 border-t border-zinc-50 text-center">
                    <button
                      onClick={() => setActiveTab('siadap')}
                      className="whitespace-nowrap text-[12px] text-zinc-400 hover:text-zinc-700 transition-colors cursor-pointer no-print"
                    >
                      + {siadapDoFunc.length - 4} avaliações mais — ver todas no separador SIADAP
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── FORMAÇÃO ── */}
        {activeTab === 'formacao' && (
          <div className="space-y-5">
            {formacoesFuncionario.length > 0 && (
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: 'ri-book-open-line', value: formacoesFuncionario.length, label: 'Ações Frequentadas', bg: 'bg-zinc-100 text-zinc-600' },
                  { icon: 'ri-time-line',       value: `${totalHorasFormacao}h`,   label: 'Total de Horas',      bg: 'bg-zinc-100 text-zinc-600' },
                  { icon: 'ri-medal-line',      value: formacoesFuncionario.filter((x) => x.participacao.certificado_emitido).length, label: 'Certificados', bg: 'bg-emerald-50 text-emerald-600' },
                ].map((s) => (
                  <div key={s.label} className="bg-white rounded-xl border border-zinc-100 p-4 flex items-center gap-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${s.bg}`}>
                      <i className={`${s.icon} text-base`} />
                    </div>
                    <div>
                      <p className="text-[20px] font-bold text-zinc-900 leading-tight">{s.value}</p>
                      <p className="text-[11px] font-medium text-zinc-500">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {formacoesFuncionario.length === 0 ? (
              <div className="bg-white rounded-2xl border border-zinc-100 py-16 flex flex-col items-center gap-3">
                <div className="w-12 h-12 flex items-center justify-center bg-zinc-50 rounded-full">
                  <i className="ri-book-open-line text-2xl text-zinc-300" />
                </div>
                <p className="text-[14px] font-medium text-zinc-400">Sem formações registadas</p>
                <p className="text-[12px] text-zinc-300">As participações em formação aparecerão aqui.</p>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-zinc-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-zinc-50 flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center bg-zinc-100 rounded-lg">
                    <i className="ri-book-open-line text-zinc-500 text-sm" />
                  </div>
                  <h2 className="text-[13px] font-semibold text-zinc-700">Histórico de Formação ({formacoesFuncionario.length})</h2>
                </div>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-100 bg-zinc-50">
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Ação de Formação</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Entidade</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Horas</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Estado</th>
                      <th className="px-5 py-3 text-left text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Nota</th>
                      <th className="px-5 py-3 text-center text-[11px] font-semibold text-zinc-400 uppercase tracking-wide">Certif.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formacoesFuncionario.map(({ participacao: p, formacao: f }, i) => {
                      const estadoCors: Record<string, string> = {
                        'Inscrito':     'bg-zinc-100 text-zinc-600',
                        'A Frequentar': 'bg-amber-50 text-amber-700',
                        'Concluído':    'bg-emerald-50 text-emerald-700',
                        'Desistiu':     'bg-rose-50 text-rose-600',
                        'Reprovado':    'bg-orange-50 text-orange-700',
                      };
                      return (
                        <tr key={p.id} className={`border-b border-zinc-50 hover:bg-zinc-50/60 transition-colors ${i % 2 === 0 ? '' : 'bg-zinc-50/20'}`}>
                          <td className="px-5 py-3.5">
                            <p className="text-[13px] font-semibold text-zinc-800">{f?.nome ?? '—'}</p>
                            {f?.categoria && (
                              <span className="text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full font-medium">{f.categoria}</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5">
                            <p className="text-[12px] text-zinc-600">{f?.entidade_formadora ?? '—'}</p>
                            {f?.data_inicio && (
                              <p className="text-[10px] text-zinc-400">{new Date(f.data_inicio).toLocaleDateString('pt-PT', { month: 'short', year: 'numeric' })}</p>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            <span className="text-[13px] font-bold text-zinc-700">{f?.duracao_horas ?? '—'}h</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${estadoCors[p.estado_participacao] ?? 'bg-zinc-100 text-zinc-600'}`}>
                              {p.estado_participacao}
                            </span>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className="text-[12px] text-zinc-600">{p.nota_final ?? '—'}</span>
                          </td>
                          <td className="px-5 py-3.5 text-center">
                            {p.certificado_emitido ? (
                              <div className="w-6 h-6 flex items-center justify-center mx-auto">
                                <i className="ri-medal-line text-base text-emerald-500" />
                              </div>
                            ) : (
                              <span className="text-[12px] text-zinc-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── SIADAP ── */}
        {activeTab === 'siadap' && id && (
          <SiadapTab
            funcionarioId={id}
            pontosDescontados={pontosDescontados}
          />
        )}
      </div>

      {/* ── PRINT FOOTER (só visível na impressão) ── */}
      <div className="print-only px-8 py-4 mt-6 border-t border-zinc-200">
        <div className="flex items-center justify-between text-[10px] text-zinc-400">
          <span>{settings.nome_camara || 'Câmara Municipal'} · SIADAP para RH</span>
          <span>Documento gerado automaticamente em {new Date().toLocaleDateString('pt-PT')} às {new Date().toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <p className="text-[9px] text-zinc-300 mt-1">Este documento é de caráter informativo e não substitui documentos oficiais.</p>
      </div>

      {/* Modal de ajuste manual de pontos */}
      {showPontosManuais && funcionario && (
        <PontosManuaisModal
          funcionario={funcionario}
          pontosCalculados={pontosAtuais}
          isPrepav={isPrepav}
          onClose={() => setShowPontosManuais(false)}
          onSuccess={() => {
            setShowPontosManuais(false);
            setRefreshKey((k) => k + 1);
            reloadSiadap();
            reloadFuncionarios();
          }}
        />
      )}

      {/* Modal de registar progressão */}
      {showRegistarProgressao && funcionario && (
        <RegistarProgressaoModal
          funcionario={funcionario}
          pontosCalculados={pontosAtuais}
          limiarEfetivo={limiar}
          jaUsouAcelerador={jaUsouAcelerador}
          onClose={() => setShowRegistarProgressao(false)}
          onSuccess={(nome) => {
            setShowRegistarProgressao(false);
            setProgressaoSuccessMsg(`Progressão de ${nome} registada com sucesso!`);
            setTimeout(() => setProgressaoSuccessMsg(''), 4000);
            setRefreshKey((k) => k + 1);
            reloadSiadap();
            reloadFuncionarios();
          }}
        />
      )}
    </main>
  );
}
